import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { MessageBubble } from './MessageBubble';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
`;
const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
`;
const TypingIndicator = styled.div`
  padding: 8px 16px;
  font-size: 13px;
  color: #666;
  font-style: italic;
  background-color: #fff;
  border-top: 1px solid #e0e0e0;
`;
const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #666;
`;
const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #dc3545;
  padding: 20px;
  text-align: center;
`;
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #999;
  padding: 20px;
  text-align: center;
`;
const ScrollAnchor = styled.div`
  height: 1px;
`;
/**
 * ChatConversation Component
 * Displays the conversation messages with auto-scroll to bottom
 */ export const ChatConversation = ({ roomId, messages, onSendMessage, currentUserId, loading = false, error = null })=>{
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);
    // Auto-scroll to bottom on new messages
    useEffect(()=>{
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: 'smooth'
            });
        }
    }, [
        messages
    ]);
    // Auto-scroll to bottom on initial load
    useEffect(()=>{
        if (containerRef.current && messages.length > 0) {
            const container = containerRef.current;
            container.scrollTop = container.scrollHeight;
        }
    }, [
        roomId
    ]);
    if (loading) {
        return /*#__PURE__*/ _jsx(Container, {
            children: /*#__PURE__*/ _jsx(LoadingState, {
                "data-testid": "chat-conversation-loading",
                children: "Loading messages..."
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(Container, {
            children: /*#__PURE__*/ _jsxs(ErrorState, {
                "data-testid": "chat-conversation-error",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Failed to load messages"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        style: {
                            fontSize: '14px',
                            marginTop: '8px'
                        },
                        children: error.message || 'An unknown error occurred'
                    })
                ]
            })
        });
    }
    if (!messages || messages.length === 0) {
        return /*#__PURE__*/ _jsx(Container, {
            children: /*#__PURE__*/ _jsxs(EmptyState, {
                "data-testid": "chat-conversation-empty",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "No messages yet"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        style: {
                            fontSize: '14px',
                            marginTop: '8px'
                        },
                        children: "Start the conversation by sending a message"
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(Container, {
        "data-testid": "chat-conversation",
        children: /*#__PURE__*/ _jsxs(MessagesContainer, {
            ref: containerRef,
            children: [
                messages.map((message, index)=>/*#__PURE__*/ _jsx(MessageBubble, {
                        message: message,
                        isOwn: message.sender?.id === currentUserId,
                        "data-testid": `chat-message-${index}`
                    }, message.id || `msg-${index}`)),
                /*#__PURE__*/ _jsx(ScrollAnchor, {
                    ref: messagesEndRef
                })
            ]
        })
    });
};
